import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateShipping } from "../slices/cartSlice"; // Import updateShipping action
import { useNavigate } from "react-router-dom";
import {
  // useFetchCartQuery,
  useFetchDeliveryAreasQuery,
  useCreateOrderMutation,
} from "../slices/checkoutSlice";

import { useFetchCustomerProfileQuery } from "../slices/customerProfileSlice";

import Button from "../components/utilComponents/Button";
import Input from "../components/utilComponents/Input";
import CustomDropdown from "../components/utilComponents/CustomDropdown";
import Success from "../components/message/Success";
import Error from "../components/message/Error";
import Loader from "../components/utilComponents/Loader";

const Shipping = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems, totalPrice, shipping, taxRate } = useSelector(
    (state) => state.cart,
  );

  const { data: customerProfile } = useFetchCustomerProfileQuery();
  const [createOrder, { isLoading: orderLoading }] = useCreateOrderMutation();
  const {
    data: areas,
    isLoading: areasLoading,
    error: areasError,
  } = useFetchDeliveryAreasQuery(cartItems[0]?.product?.location, {
    skip: !cartItems.length,
  });

  const [selectedArea, setSelectedArea] = useState("");
  const [deliveryAreas, setDeliveryAreas] = useState([]);
  const [formState, setFormState] = useState({
    state: "",
    area: "",
    address: "",
    postcode: "",
    phoneNumber: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const taxAmount = parseFloat(((taxRate / 100) * totalPrice).toFixed(2));
  const grandTotal = (
    parseFloat(totalPrice) + // Subtotal
    parseFloat(shipping) + // Shipping Fee
    taxAmount
  ).toFixed(2);

  // Pre-fill state and address information
  useEffect(() => {
    if (cartItems.length) {
      setFormState((prev) => ({
        ...prev,
        state: cartItems[0]?.product?.location || "",
      }));
    }
  }, [cartItems]);

  useEffect(() => {
    if (customerProfile) {
      setFormState((prev) => ({
        ...prev,
        address: customerProfile.address?.address || "",
        postcode: customerProfile.address?.postcode || "",
        phoneNumber: customerProfile.phoneNumber || "",
      }));
    }
  }, [customerProfile]);

  // Set delivery areas
  useEffect(() => {
    if (areas) {
      setDeliveryAreas(
        areas.map((area) => `${area.area} - ₦${area.price.toFixed(2)}`),
      );
    }
    if (areasError) {
      setErrorMessage("Failed to fetch delivery areas.");
    }
  }, [areas, areasError]);

  const handleInputChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleAreaSelection = (area) => {
    const shippingCost = parseFloat(area.split(" - ₦")[1] || "0");
    setSelectedArea(area);

    dispatch(
      updateShipping({
        shipping: shippingCost,
      }),
    );
  };

  const handleSubmit = async () => {
    if (
      !selectedArea ||
      !formState.address ||
      !formState.postcode ||
      !formState.phoneNumber
    ) {
      setErrorMessage("All fields are required.");
      return;
    }

    try {
      const response = await createOrder({
        state: formState.state, // Include the state field
        area: selectedArea.split(" - ")[0],
        address: formState.address,
        postcode: formState.postcode,
        phoneNumber: formState.phoneNumber,
        shipping: shipping,
      }).unwrap();

      setSuccessMessage(response.message);

      // Navigate to Order Review page with orderId
      navigate(`/checkout/review/${response.order.orderId}`);
    } catch (error) {
      setErrorMessage(error.data?.message || "Failed to create order.");
    }
  };

  // if (areasLoading || orderLoading) return <div>Loading...</div>;

  if (areasLoading || orderLoading) {
    return (
      <div>
        <div className=" md:hidden mx-2">
          <Loader count={1} columns={1} itemClass="h-[320px]" />
          <Loader count={1} columns={1} itemClass="h-[200px]" />
        </div>
        <div className="hidden md:block container">
          <Loader count={2} columns={2} itemClass="h-[320px]" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Desktop Section */}
      <div className="hidden lg:flex m-2 md:container flex flex-col space-y-4 md:flex-row md:space-x-8 md:space-y-0">
        {/* Shipping Section */}
        <div className="flex-4 order-summary">
          <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4">
            Shipping Information
          </h3>
          <Input name="state" value={formState.state} disabled />

          <div className="mb-4">
            <CustomDropdown
              options={deliveryAreas}
              value={selectedArea}
              onChange={handleAreaSelection}
              placeholder="Select Area"
            />
          </div>
          <Input
            name="address"
            value={formState.address}
            onChange={handleInputChange}
            placeholder="Enter Address"
          />
          <Input
            name="postcode"
            value={formState.postcode}
            onChange={handleInputChange}
            placeholder="Enter Postcode"
          />
          <Input
            name="phoneNumber"
            value={formState.phoneNumber}
            onChange={handleInputChange}
            placeholder="Enter Phone Number"
          />
        </div>
        {/* Order Summary Section */}
        {/* <div className="flex-2 w-full lg:w-[30vw]">
          <div className="order-summary">
            <h3 className="font-nunito text-agBlack font-semibold text-lg">
              Order Summary
            </h3>
            <hr className="mb-4" />
            <p>Subtotal: ₦{totalPrice.toLocaleString("en-NG")}</p>
            <p>Shipping: ₦{shipping.toLocaleString("en-NG")}</p>
            <p>
              Tax ({taxRate}%): ₦{((taxRate / 100) * totalPrice).toFixed(2)}
            </p>
            <p>Total: ₦{grandTotal.toLocaleString("en-NG")}</p>
            <Button variant="primary" fullWidth onClick={handleSubmit}>
              CONTINUE
            </Button>
          </div>
        </div> */}
        <div className="flex-2 w-full lg:w-[30vw]">
          <div className="order-summary">
            <h3 className="font-nunito text-agBlack font-semibold text-lg">
              Order Summary
            </h3>
            <hr className="mb-4" />
            <div className="order-summary-details">
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Subtotal</span>
                <span className="text-sm text-agBlack">
                  ₦{totalPrice.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Shipping</span>
                <span className="text-sm text-agBlack">
                  ₦{shipping.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">
                  Tax ({taxRate}%)
                </span>
                <span className="text-sm text-agBlack">
                  ₦{((taxRate / 100) * totalPrice).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total</span>
                <span className="text-agBlack font-nunito font-bold text-lg">
                  ₦{grandTotal.toLocaleString("en-NG")}
                </span>
              </div>
            </div>
            <Button variant="primary" fullWidth onClick={handleSubmit}>
              CONTINUE
            </Button>
          </div>
        </div>

        {successMessage && (
          <Success
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
        {errorMessage && (
          <Error message={errorMessage} onClose={() => setErrorMessage("")} />
        )}
      </div>
      {/* Mobile Section */}
      <div className="lg:hidden m-2 flex flex-col space-y-4 ">
        {/* Shipping Section */}
        <div>
          <h3 className="font-nunito text-agBlack font-semibold text-lg">
            Shipping Information
          </h3>
          <Input name="state" value={formState.state} disabled />

          <div className="mb-4">
            <CustomDropdown
              options={deliveryAreas}
              value={selectedArea}
              onChange={handleAreaSelection}
              placeholder="Select Area"
            />
          </div>
          <Input
            name="address"
            value={formState.address}
            onChange={handleInputChange}
            placeholder="Enter Address"
          />
          <Input
            name="postcode"
            value={formState.postcode}
            onChange={handleInputChange}
            placeholder="Enter Postcode"
          />
          <Input
            name="phoneNumber"
            value={formState.phoneNumber}
            onChange={handleInputChange}
            placeholder="Enter Phone Number"
          />
        </div>
        {/* Order Summary Section */}
        <div className="flex-2 w-full lg:w-[30vw]">
          <div className="order-summary">
            <h3 className="font-nunito text-agBlack font-semibold text-lg">
              Order Summary
            </h3>
            <hr className="mb-4" />
            <div className="order-summary-details">
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Subtotal</span>
                <span className="text-sm text-agBlack">
                  ₦{totalPrice.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Shipping</span>
                <span className="text-sm text-agBlack">
                  ₦{shipping.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">
                  Tax ({taxRate}%)
                </span>
                <span className="text-sm text-agBlack">
                  ₦{((taxRate / 100) * totalPrice).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total</span>
                <span className="text-agBlack font-nunito font-bold text-lg">
                  ₦{grandTotal.toLocaleString("en-NG")}
                </span>
              </div>
            </div>
            <Button variant="primary" fullWidth onClick={handleSubmit}>
              CONTINUE
            </Button>
          </div>
        </div>
        {successMessage && (
          <Success
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
        {errorMessage && (
          <Error message={errorMessage} onClose={() => setErrorMessage("")} />
        )}
      </div>
    </div>
  );
};

export default Shipping;
